import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { FORM_ELEMENT_EMPTY } from 'constants/FormConstants';
import { AppUser, AppUsersResponse } from 'features/appUser/services/getAppUsers';
import { TAG_PLACES } from 'services/TagsConstants';

import { createQueryString, handleDuplicates } from '../../../utils/helpers';
import { ProductRatingsContentDTO } from './getProductRatings';
import { Products } from './getProducts';

const BASE_URL = '/products/ratings';

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
  build.query<Ratings, RatingParameters>({
    async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
      const { place, ...params } = _arg;
      const productsRatingResponse = await fetchWithBQ(
        createQueryString(params, `${BASE_URL}?place=${place}`),
      );
      const ratingData = productsRatingResponse.data as Ratings;
      const users = ratingData.content.map((rating: Rating) => {
        return rating?.createdBy;
      });
      const usersIds = handleDuplicates(users);
      const usersResponse = await fetchWithBQ(
        `/app-users?size=${usersIds?.length}&ids=${usersIds}`,
      );
      const userData = usersResponse.data as AppUsersResponse;
      const productId = ratingData.content.map((rating: Rating) => {
        return rating?.product;
      });
      const productsResponse = await fetchWithBQ(`/products?ids=${productId}`);
      const productsData = productsResponse.data as Products;

      const ratings = ratingData.content.map((rating: Rating) => {
        const matchedUser = userData.content.find((user) => user?.uuid === rating?.createdBy);
        const matchedProduct = productsData.content.find(
          (product) => product?.uuid === rating?.product,
        );
        return {
          ...rating,
          product: matchedProduct?.name,
          createdAt: new Date(rating.createdAt).toLocaleString(),
          createdBy: `${matchedUser?.firstName} ${matchedUser?.lastName}`,
          user: matchedUser,
          comment: rating?.comment || FORM_ELEMENT_EMPTY,
        };
      });
      return {
        data: {
          content: ratings,
          totalElements: ratingData?.totalElements,
          totalPages: ratingData?.totalPages,
        },
      };
    },
    providesTags: [TAG_PLACES],
  });

export type Rating = ProductRatingsContentDTO & {
  product?: string;
};

export type Ratings = {
  content: Rating[];
  totalElements: number;
  totalPages: number;
  users?: AppUser[];
};

export type RatingParameters = {
  place?: string;
  user?: string;
};
